import { createSortOption } from "./utils/sorting"
import { thumbnailDecorator } from "@nosto/search-js/thumbnails"
import { priceDecorator } from "@nosto/search-js/currencies"
import { AutocompleteConfig } from "@nosto/search-js/preact/autocomplete"
import { SerpConfig } from "@nosto/search-js/preact/serp"
import { handleDecorator, styleFamilyDecorator } from "./decorators"
import { CategoryConfig } from "@nosto/search-js/preact/category"
import { SearchQuery } from "@nosto/nosto-js/client"
import { resolveCategoryIdForQuery } from "./utils/resolveCategoryContext"
import { pipe } from "./utils/pipe"
import { tagging } from "./mapping/tagging"

/**
 * Account base currency. Nosto indexes prices in this currency; per-visitor
 * currencies are derived via exchange rates configured in
 * Settings > Other > Multi-Currency.
 */
const defaultCurrency = "USD"

/**
 * Feature gate for passing the visitor's active currency to Nosto search.
 *
 * Sending `currency` is only safe once Nosto has exchange rates configured
 * for that currency — otherwise search fails with `No exchange rates
 * configured`. Rates are configured in Nosto admin
 * (Settings > Other > Multi-Currency). Flip to `true` once those rates are in
 * place for the storefront currencies.
 */
const multiCurrencyEnabled = false

/**
 * Reads the visitor's active currency from page tagging, with safe fallback to
 * `defaultCurrency`. The tagging accessors throw before `nostojs` resolves
 * (e.g. server-side tooling, vitest environment), so this swallow + fallback
 * keeps the search query construction pure and test-safe.
 */
function activeCurrency(): string {
  try {
    return tagging.variation() ?? defaultCurrency
  } catch {
    return defaultCurrency
  }
}

/**
 * Returns the search-query `currency` value when multi-currency is enabled and
 * the visitor's active currency differs from the base. Returns `undefined`
 * otherwise so the field is omitted entirely — Nosto then serves prices in
 * the base currency without attempting exchange-rate conversion.
 */
function searchCurrency(): string | undefined {
  if (!multiCurrencyEnabled) return undefined
  const active = activeCurrency()
  return active === defaultCurrency ? undefined : active
}

export const sizes = [24, 48, 72]

export const sortOptions = [
  createSortOption("score", "Relevance", { field: "_score", order: "desc" }),
  createSortOption("whatsNew", "What's New", { field: "whatsNew", order: "desc" }),
  createSortOption("price", "Price Low to High", { field: "price", order: "asc" }),
  createSortOption("-price", "Price High to Low", { field: "price", order: "desc" }),
  createSortOption("topSellers", "Top Sellers", { field: "topSellers", order: "desc" })
]

/**
 * Enable or disable infinite scroll for search results
 */
export const infiniteScroll = false

/**
 * Enable or disable skeleton loading state for search results
 */
export const skeletonLoading = true

/**
 * Enable or disable search redirect on search form submit
 */
export const redirectOnSearch = true

export const shopifyMode = !!window.Shopify

/**
 * Default sort option for search results and categories
 */
export const defaultSort = sortOptions[0]

/**
 * Default page size for search results and categories
 */
export const defaultSize = sizes[0]

/**
 * When Nosto's raw `products.total` is at or below this cap, the app fetches all
 * raw hits, applies style-family dedupe once, and paginates in memory so counts,
 * page size, and page numbers match visible (deduped) products.
 */
export const styleFamilyCatalogMaxRawTotal = 2500

/**
 * Chunk size for sequential `search()` calls while building the full catalog.
 */
export const styleFamilyCatalogChunkSize = 250

/**
 * Search GraphQL does **not** accept `variables.products.fields`
 * (`docs/live-data-validation/search-field-error.json`).
 * Field selection is driven by the embedded client hit projection instead — never attach `fields` here.
 */

/**
 * Path to the search page
 */
export const searchPath = "/search"

/**
 * Get the search path with the locale prefix that is present in the current URL.
 * Keeps search redirects in the current locale context by reusing the prefix the
 * page is already on rather than synthesizing one from a country code. The base
 * market (no prefix) returns the bare "/search" — so this is correct with or
 * without an en-us slug.
 *
 * @example
 * // On /en-ca/collections/shirts → "/en-ca/search"
 * // On /collections/shirts (base market) → "/search"
 */
export function getLocalizedSearchPath(): string {
  const pathname = window.location.pathname
  // Match locale prefixes like /en-ca/, /fr-ca/, etc. (also /en-ca without a
  // trailing slash, e.g. the localized home page).
  const localeMatch = pathname.match(/^\/([a-z]{2}-[a-z]{2})(\/|$)/i)
  if (localeMatch) {
    return `/${localeMatch[1]}${searchPath}`
  }
  return searchPath
}

/**
 * CSS selectors for the Injected mode
 */
export const selectors = {
  // autocomplete dropdown container
  dropdown: "#dropdown",
  // search input field
  searchInput: "#search",
  // search form element selector
  searchForm: "#search-form",
  // container for actual search/category results
  results: "#serp"
}

/**
 * Base hit decorator definitions for the search results.
 *
 * Decorators are transformer functions applied in order to all results returned
 * from the search backend. You may define your own decorators using `handleDecorator`
 * as an example.
 * The type for decorated products is defined in `src/types.ts`.
 *
 * `styleFamilyDecorator` extracts the Nosto-indexed style-family ID per Agent
 * A's data contract (`docs/agent-a-nosto-data-contract.md`). Once available
 * on `product.styleFamilyKey`, it is consumed by `dedupeByStyleFamily` to
 * collapse sibling color products to one visible card per family in the grid
 * (plan § "Style-Family Dedupe Design"). Hits without the indexed custom
 * field stay unchanged and are treated as their own family by the dedupe step.
 */
export const hitDecorators = [handleDecorator, priceDecorator(), styleFamilyDecorator] as const

/**
 * Shared configuration for all page types (Autocomplete, Category, Search)
 */
export const baseConfig = {
  search: {
    hitDecorators
  },
  queryModifications: withBaseConfig
}

/** Exported for offline tooling (`scripts/dump-withBaseConfig-search-query.mts`) and live API validation notes. */
export function withBaseConfig(query: SearchQuery) {
  const products = query.products ?? {}

  return {
    ...query,
    products: {
      size: defaultSize,
      ...products,
      // Note: do not set `products.fields` — Nosto Search GraphQL rejects `InputSearchProducts.fields`.
      // Exchange-rate based multi-currency: Nosto admin is configured with
      // primary variant USD + currency formats for each storefront currency.
      // Passing the visitor's active currency makes Nosto convert prices and
      // facet bounds before returning, so the price filter matches the
      // CAD/etc card prices the storefront renders. Gated by
      // `multiCurrencyEnabled` until exchange rates are configured in Nosto admin.
      ...(searchCurrency() ? { currency: searchCurrency() } : {})
    }
  } satisfies SearchQuery
}

/**
 * Autocomplete configuration
 */

export const autocompleteConfig = {
  ...baseConfig,
  memoryCache: true,
  historyEnabled: true,
  historySize: 10,
  search: {
    hitDecorators: [
      ...hitDecorators,
      thumbnailDecorator({ size: "8" }) // 400x400
    ]
  },
  queryModifications: pipe<SearchQuery>(withBaseConfig, withAutocompleteDefaults)
} satisfies AutocompleteConfig

function withAutocompleteDefaults(query: SearchQuery) {
  return {
    ...query,
    products: {
      ...query.products,
      size: 5
    },
    keywords: {
      fields: ["keyword", "_highlight.keyword"],
      size: 5,
      facets: ["*"]
    },
    popularSearches: {
      fields: ["query"],
      size: 5
    },
    categories: {
      fields: ["name", "url"],
      size: 5
    }
  } satisfies SearchQuery
}

/**
 * Category page configuration
 */

export const categoryConfig = {
  ...baseConfig,
  persistentSearchCache: false,
  preservePageScroll: false,
  queryModifications: pipe(withBaseConfig, withCategoryMetadata)
} satisfies CategoryConfig

export function withCategoryMetadata(query: SearchQuery) {
  // Only inject `categoryId`. Nosto indexes categories by the primary-locale
  // path, so passing the storefront's localized title (e.g. "Pantalon de
  // travail pour hommes") filters the result to zero. The numeric Shopify
  // collection ID is locale-stable and is sufficient on its own to scope the
  // PLP — `categoryPath` is redundant for English and breaks every other
  // locale.
  const categoryId = resolveCategoryIdForQuery()
  const modifiedQuery = {
    ...query,
    products: {
      ...query.products,
      categoryId
    }
  } satisfies SearchQuery

  return modifiedQuery
}

/**
 * Search result page (serp) configuration
 */
export const serpConfig = {
  ...baseConfig,
  persistentSearchCache: false,
  preservePageScroll: false
} satisfies SerpConfig
